import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../ui/Input/Input';
import { cn } from '@hero/web-lib';

interface WeightEditProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  unit?: string;
  className?: string;
  name?: string;
}

export const WeightEdit: React.FC<WeightEditProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  unit = 'ton',
  className = '',
  name,
}) => {
  const { t } = useTranslation(['m-order', 'c-main']);
  const [inputValue, setInputValue] = useState<string>('');
  const isUserTypingRef = useRef<boolean>(false);
  const lastClearedRef = useRef<boolean>(false);

  // Sync inputValue with external value (only when user is not typing)
  useEffect(() => {
    if (!isUserTypingRef.current && !lastClearedRef.current) {
      if (value === undefined || value === null) {
        if (inputValue !== '') {
          setInputValue('');
        }
      } else {
        const stringValue = value.toString();
        if (stringValue !== inputValue) {
          setInputValue(stringValue);
        }
      }
    }
    // Reset the cleared flag after sync
    if (lastClearedRef.current && (value === undefined || value === null)) {
      lastClearedRef.current = false;
    }
  }, [value, inputValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUserTypingRef.current = true;
    const inputValue = e.target.value;

    // Allow empty value for clearing
    if (inputValue === '') {
      setInputValue('');
      onChange(undefined);
      lastClearedRef.current = true;
      setTimeout(() => {
        isUserTypingRef.current = false;
      }, 150);
      return;
    }

    // Only allow numeric input (including decimals)
    if (/^\d*\.?\d*$/.test(inputValue)) {
      setInputValue(inputValue);
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue >= 0) {
        onChange(numValue);
      } else {
        // For partial numbers like "1." or "0.", still allow typing
        onChange(undefined);
      }
    }

    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 150);
  };

  const handleBlur = () => {
    // On blur, ensure we have a valid number or clear it
    if (inputValue === '' || inputValue === '.') {
      onChange(undefined);
      setInputValue('');
    } else {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue >= 0) {
        onChange(numValue);
        setInputValue(numValue.toString());
      } else {
        onChange(undefined);
        setInputValue('');
      }
    }
  };

  return (
    <div className={cn('space-y-1', className)}>
      <div className="relative rounded-md border border-input bg-background overflow-visible">
        {/* Unit Display */}
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-10">
          <span className="text-sm font-medium text-muted-foreground">{unit}</span>
        </div>

        <Input
          type="text"
          name={name}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={
            placeholder || t('m-order:load_details.weight_placeholder', { defaultValue: 'Weight' })
          }
          disabled={disabled}
          className="pl-12 border-0 bg-transparent focus:ring-0 focus:border-0 h-10"
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
